import reviewData from "./reviewCount.json";

/**
 * 고이 상담, 후기 지표.
 *
 * 숫자는 ParlorHero, ParlorSidebar 가 노출 중인 것과 같다. 표기만 "+" 에서 "이상" 으로
 * 바꿨다(영민 지정). 한 사이트 안에서 숫자가 갈리면 안 되므로 여기 한 곳에 모아 둔다.
 *
 * reviewCount 는 goi_common 의 실데이터(집계 스크립트가 갱신)를 그대로 읽는다.
 * platform 의 richSearch 도 같은 파일로 ratingCount 를 만든다.
 */
export const GOI_TRUST = {
  consultLabel: "장례, 상조 상담",
  consultValue: "22,000명 이상",
  scoreLabel: "후기 평균",
  scoreValue: "4.9",
  funeralLabel: "장례 진행",
  funeralValue: "1,900건 이상",
  reviewCount: reviewData.count,
  note: "26년도 최근 3개월 기준, 고이장례연구소 이용 후기입니다.",
} as const;

/**
 * 무빈소 기준 지표. `GOI_TRUST` 는 고이 전체 숫자라 무빈소 화면에서 쓰면 맥락이 어긋난다.
 *
 * 이 화면에 온 사람이 확인하고 싶은 것은 "무빈소로 해도 괜찮은가" 하나다. 전체 상담 22,000명과
 * 전체 장례 1,900건은 그 질문에 답하지 않는다.
 *
 * 출처 (BigQuery `platform-data-381405.public`, 2026-09-11 조회)
 * - 진행 건수: `user_conductedfuneral` 의 `service_product LIKE '%무빈소%'` 889건
 *   (2024-11-29 ~ 2026-09-11). `sangjo_type` 은 2023년 이후 안 채워져 쓸 수 없다
 * - 후기, 평점: 위 장례에 붙은 `funeral_sangjoreview` 중 `is_excluded` 아닌 404건, 평균 4.98
 *
 * `service_product` 가 비어 있는 장례가 232건 있어 889는 **하한**이다. 미분류분 중 무빈소가
 * 섞여 있을 수 있고 월 100건 안팎으로 늘고 있어 표기는 `1,000건 이상` 으로 둔다(영민 지정,
 * 2026-09-11). 조회 시점 실측은 889건이므로 다음 갱신 때 1,000을 넘었는지 다시 확인한다.
 */
export const MUBINSO_TRUST = {
  funeralLabel: "무빈소 장례 진행",
  funeralValue: "1,000건 이상",
  scoreLabel: "후기 평균",
  scoreValue: "4.98",
  reviewLabel: "무빈소 후기",
  reviewValue: "400건 이상",
  note: "2024년 11월 이후 고이에서 진행한 무빈소 장례 기준입니다.",
} as const;

export interface MubinsoReview {
  /** 실제 후기 id. www.goifuneral.co.kr/review/{id}/ 로 연결된다 */
  id: number;
  text: string;
  author: string;
  date: string;
  /** 후기 작성자가 실제로 매긴 별점. 지어낸 값이 아니라 API 의 score 그대로 */
  score: number;
}

/**
 * 무빈소로 진행한 가족의 후기.
 *
 * platform 의 무빈소 퍼널(?template=mubinso) 전환 화면이 노출 중인 것과 같은 후기다.
 * 무빈소와 무관한 일반 후기를 섞지 않는다. 이 페이지에 들어온 사람이 확인하고 싶은 것은
 * "무빈소로 해도 괜찮은가" 하나다.
 *
 * 인용문은 퍼널이 쓰는 발췌본 그대로이고, id 와 score 는 원본 후기에서 확인했다.
 * 작성일이 같은 후기가 3~9건씩 있어 날짜만으로는 특정되지 않으므로,
 * 인용문의 핵심 문구가 본문에 전부 들어있는 1건으로 확정했다.
 *
 * 출처: MubinsoCostFunnelA.tsx, MubinsoProcedureFunnelB.tsx
 */
export const MUBINSO_REVIEWS: MubinsoReview[] = [
  {
    id: 2308,
    text: "무빈소 장례지만 깊은 감동을 받았어요. 추가금 없이 진행해 주셨습니다.",
    author: "김OO 고객님",
    date: "2026. 06. 30",
    score: 5,
  },
  {
    id: 2152,
    text: "물품 변경과 선택이 자유롭고, 가심비 좋고 합리적인 가격이라 마음에 쏙 들었어요.",
    author: "조OO 고객님",
    date: "2026. 05. 31",
    score: 5,
  },
  {
    id: 2060,
    text: "생소하거나 무성의할까 우려했는데, 그 어떤 화려한 장례보다도 정중하고 기품 있게 이끌어주셨어요.",
    author: "이OO 고객님",
    date: "2026. 05. 11",
    score: 5,
  },
  {
    id: 1809,
    text: "가장 중요한 입관, 발인 예식을 위해 새벽부터 꽃시장에서 생화를 넉넉히 준비해주셔서, 꽃집을 했던 누님이 많이 만족했어요.",
    author: "구OO 고객님",
    date: "2026. 03. 13",
    score: 5,
  },
];
